import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@material-ui/styles";
import { debounce, uniqBy } from "lodash";
import {
  FormLabel,
  TextInput,
  SelectInput,
  AutoCompleteWithSearch,
  PrimaryButton,
  SecondaryButton,
  ModalError,
  ModalSuccess,
} from "components";
import { getErrors } from "utils";
import {
  getDspSFTPConfig,
  createDspSFTPConfig,
  updateDspSFTPConfig,
  testDspSFTPConnection,
  getDspOptions,
} from "utils/DSPSFTPService";

const useStyles = makeStyles(() => ({
  dialogTitle: {
    padding: "24px 24px 16px",
  },
  dialogContent: {
    padding: "0px 24px 16px",
  },
  dialogActions: {
    padding: "16px 24px 24px",
    justifyContent: "flex-end",
    gap: "10px",
  },
  testButton: {
    textTransform: "none",
    borderRadius: "6px",
    padding: "6px 20px",
    height: "36px",
    color: "#ffffff",
    backgroundColor: "#74D898",
    fontWeight: 500,
    fontSize: "14px",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#5EC78A",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "#A7F3D0",
      color: "#ffffff",
    },
  },
  privateKeyInput: {
    "& .MuiInputBase-root": {
      alignItems: "flex-start",
    },
    "& .MuiInputBase-input": {
      fontFamily: "monospace, Courier, 'Courier New'",
      fontSize: "12px",
      lineHeight: "1.4",
      maxHeight: "110px",
      overflowY: "auto !important",
      overflowX: "auto !important",
      whiteSpace: "pre",
      wordBreak: "normal",
      scrollbarWidth: "thin",
      "&::-webkit-scrollbar": {
        width: "6px",
        height: "6px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#D1D5DB",
        borderRadius: "4px",
      },
    },
  },
}));

const AUTH_TYPES = [
  { value: "password", label: "Direct Password" },
  { value: "key_bastion", label: "Bastion Host" },
];

const ACTION_MODES = [
  { value: "all", label: "All" },
  { value: "download", label: "Download" },
  { value: "upload", label: "Upload" },
];

const EMPTY_FORM = {
  dsp_id: "",
  name: "",
  slug: "",
  action: "",
  action_mode: "",
  host: "",
  port: "",
  username: "",
  password: "",
  private_key: "",
  bastion_host: "",
  bastion_port: "",
  bastion_username: "",
  bastion_password: "",
  bastion_private_key: "",
  remote_path: "",
  auth_type: "password",
  is_active: true,
};

function CreateOrEditDspSftpModal({ open, editItem, onClose, onSaved }) {
  const classes = useStyles();
  const [form, setForm] = useState(EMPTY_FORM);
  const [dspOptions, setDspOptions] = useState([]);
  const [isLoadingDsp, setIsLoadingDsp] = useState(false);
  const [hasMoreDsp, setHasMoreDsp] = useState(true);
  const [dspQueryParams, setDspQueryParams] = useState({
    page: 1,
    per_page: 10,
    search: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const editId = editItem?.id || editItem?.dsp_id;
  const isEdit = Boolean(editId);

  const normalizeAuthType = type => {
    if (
      type === "key_bastion" ||
      type === "bastion" ||
      type === "bastion_host"
    ) {
      return "key_bastion";
    }
    return "password";
  };

  const getOptionText = obj => {
    if (!obj) return "";
    if (typeof obj === "string") return obj;
    return String(
      obj.label || obj.name || obj.display_name || obj.dsp_name || ""
    );
  };

  const getOptionId = obj => {
    if (!obj) return "";
    if (typeof obj === "string" || typeof obj === "number") return String(obj);
    return String(obj.id ?? obj.dsp_id ?? "");
  };

  const fetchDspOptions = async (params = dspQueryParams) => {
    try {
      setIsLoadingDsp(true);
      const res = await getDspOptions(params);
      const resData = res?.data;
      const responseData =
        resData?.data?.data ||
        resData?.data ||
        (Array.isArray(resData) ? resData : []);
      const meta = resData?.meta || resData?.data?.meta || resData || {};
      const list = (Array.isArray(responseData) ? responseData : []).map(
        item => {
          const dspId = item?.dsp_id ?? item?.id ?? "";
          const dspName = String(
            item?.display_name ||
              item?.dsp_name ||
              item?.name ||
              item?.label ||
              ""
          );
          return {
            id: dspId,
            dsp_id: dspId,
            label: dspName,
            name: dspName,
            slug: String(item?.dsp_slug || item?.slug || ""),
            ...item,
          };
        }
      );

      if (params.page === 1) {
        setDspOptions(list);
      } else {
        setDspOptions(prev =>
          uniqBy([...prev, ...list], item => item.id || item.dsp_id)
        );
      }

      const currentLimit = Number(
        meta?.limit || params.limit || params.per_page || 10
      );
      const total = Number(meta?.total ?? meta?.found);
      const currentPage = Number(meta?.page || params.page || 1);

      if (!isNaN(total)) {
        const totalLoaded =
          currentPage === 1
            ? list.length
            : (currentPage - 1) * currentLimit + list.length;
        setHasMoreDsp(totalLoaded < total);
      } else if (meta?.total_page) {
        setHasMoreDsp(currentPage < Number(meta.total_page));
      } else {
        setHasMoreDsp(list.length >= currentLimit);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingDsp(false);
    }
  };

  const debounceFetchDsp = useCallback(
    debounce(params => {
      fetchDspOptions(params);
    }, 500),
    []
  );

  useEffect(() => {
    if (open) {
      if (dspQueryParams.page === 1) {
        debounceFetchDsp(dspQueryParams);
        return () => {
          debounceFetchDsp.cancel();
        };
      } else {
        fetchDspOptions(dspQueryParams);
      }
    } else {
      setDspQueryParams({
        page: 1,
        per_page: 10,
        search: "",
      });
      setDspOptions([]);
    }
  }, [open, dspQueryParams, debounceFetchDsp]);

  useEffect(() => {
    const fetchDetail = async () => {
      if (open && isEdit) {
        setIsLoadingDetail(true);
        try {
          const res = await getDspSFTPConfig(editId);
          const d = res?.data?.data || editItem || {};
          setForm({
            dsp_id: d.dsp_id || "",
            name: d.dsp_name || d.name || "",
            slug: d.dsp_slug || d.slug || "",
            action: d.action || d.action_mode || "",
            action_mode: d.action || d.action_mode || "",
            host: d.host || "",
            port: d.port !== undefined && d.port !== null ? String(d.port) : "",
            username: d.username || "",
            password: d.password || "",
            private_key: d.private_key || "",
            bastion_host: d.bastion_host || "",
            bastion_port:
              d.bastion_port !== undefined && d.bastion_port !== null
                ? String(d.bastion_port)
                : "",
            bastion_username: d.bastion_username || "",
            bastion_password: d.bastion_password || d.bastion_pass || "",
            bastion_private_key: d.bastion_private_key || "",
            remote_path: d.remote_path || "",
            auth_type: normalizeAuthType(d.auth_type),
            is_active: d.is_active ?? true,
          });
        } catch (error) {
          const d = editItem || {};
          setForm({
            dsp_id: d.dsp_id || "",
            name: d.dsp_name || d.name || "",
            slug: d.dsp_slug || d.slug || "",
            action: d.action || d.action_mode || "",
            action_mode: d.action || d.action_mode || "",
            host: d.host || "",
            port: d.port !== undefined && d.port !== null ? String(d.port) : "",
            username: d.username || "",
            password: d.password || "",
            private_key: d.private_key || "",
            bastion_host: d.bastion_host || "",
            bastion_port:
              d.bastion_port !== undefined && d.bastion_port !== null
                ? String(d.bastion_port)
                : "",
            bastion_username: d.bastion_username || "",
            bastion_password: d.bastion_password || d.bastion_pass || "",
            bastion_private_key: d.bastion_private_key || "",
            remote_path: d.remote_path || "",
            auth_type: normalizeAuthType(d.auth_type),
            is_active: d.is_active ?? true,
          });
        } finally {
          setIsLoadingDetail(false);
        }
      } else if (open && !isEdit) {
        setForm(EMPTY_FORM);
      }
    };

    fetchDetail();
  }, [open, editId, isEdit, editItem]);

  const setField = (key, value) => {
    setForm(prev => ({
      ...prev,
      [key]: value,
      ...(key === "action" && { action_mode: value }),
      ...(key === "action_mode" && { action: value }),
    }));
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      ModalError("DSP is required");
      return;
    }
    if (!form.slug?.trim()) {
      ModalError("DSP Slug is required");
      return;
    }
    if (!form.host?.trim()) {
      ModalError("Destination Host (Target) is required");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ...form,
        display_name: form.name,
        dsp_slug: form.slug,
        dsp_id: form.dsp_id || editItem?.dsp_id,
        port: Number(form.port) || 22,
        bastion_port: Number(form.bastion_port) || 22,
        action: form.action || form.action_mode || "all",
      };
      if (isEdit) {
        await updateDspSFTPConfig(editId, payload);
      } else {
        await createDspSFTPConfig(payload);
      }
      ModalSuccess(
        `DSP SFTP configuration ${isEdit ? "updated" : "created"} successfully.`
      );
      if (onSaved) onSaved();
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!form.host?.trim()) {
      ModalError(
        "Destination Host (Target) is required before testing the connection."
      );
      return;
    }

    setIsTesting(true);
    try {
      const payload = {
        ...form,
        display_name: form.name,
        dsp_slug: form.slug,
        dsp_id: form.dsp_id || editItem?.dsp_id,
        port: Number(form.port) || 22,
        bastion_port: Number(form.bastion_port) || 22,
        action: form.action || form.action_mode || "all",
      };

      const res = await testDspSFTPConnection(payload);

      ModalSuccess(res?.data?.message || "Connection successful!");
    } catch (error) {
      ModalError(error?.response?.data?.message || getErrors(error?.response || error));
    } finally {
      setIsTesting(false);
    }
  };

  const isBastion =
    form.auth_type === "key_bastion" || form.auth_type === "bastion";
  const isLoading = isSaving || isTesting || isLoadingDetail || isLoadingDsp;

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "12px",
          maxWidth: "680px",
        },
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6" fontWeight="bold" fontSize="18px">
          SFTP DSP Configuration
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2.5}>
          {/* Row 1: DSP & DSP Slug */}
          <Grid item xs={12} sm={6}>
            <FormLabel label="DSP" />
            <AutoCompleteWithSearch
              placeholder="Select DSP"
              options={dspOptions}
              uniqueKey="id"
              optionLabel="label"
              disabled={isLoading}
              value={
                dspOptions.find(opt => {
                  const optId = getOptionId(opt);
                  const formDspId = form.dsp_id ? String(form.dsp_id) : "";
                  if (formDspId && optId && optId === formDspId) return true;
                  const optText = getOptionText(opt);
                  const formName = form.name ? String(form.name) : "";
                  if (
                    formName &&
                    optText &&
                    optText.toLowerCase() === formName.toLowerCase()
                  )
                    return true;
                  return false;
                }) ||
                (form.name
                  ? {
                      id: form.dsp_id || form.name,
                      dsp_id: form.dsp_id || form.name,
                      label: String(form.name),
                      name: String(form.name),
                    }
                  : null)
              }
              onChange={selected => {
                if (selected) {
                  const selectedName = getOptionText(selected);
                  setForm(prev => ({
                    ...prev,
                    dsp_id: selected.dsp_id || selected.id,
                    name: selectedName,
                  }));
                } else {
                  setForm(prev => ({
                    ...prev,
                    dsp_id: "",
                    name: "",
                  }));
                }
              }}
              onInputChange={(newInput, reason) => {
                if (reason === "input" || reason === "clear") {
                  setDspQueryParams(prev => ({
                    ...prev,
                    page: 1,
                    search: newInput || "",
                  }));
                }
              }}
              isOptionEqualToValue={(option, val) => {
                if (!option || !val) return false;
                const optId = getOptionId(option);
                const valId = getOptionId(val);
                if (optId && valId) {
                  return optId === valId;
                }
                const optText = getOptionText(option);
                const valText = getOptionText(val);
                if (optText && valText) {
                  return optText.toLowerCase() === valText.toLowerCase();
                }
                return false;
              }}
              hasMore={hasMoreDsp}
              loading={isLoadingDsp}
              setQueryParams={setDspQueryParams}
              size="small"
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormLabel label="DSP Slug" />
            <TextInput
              value={form.slug}
              onChange={e => setField("slug", e.target.value)}
              placeholder="spotify_global_sync"
              width="100%"
              disabled={isLoading}
            />
          </Grid>

          {/* Row 2: Action Mode & Authentication Type */}
          <Grid item xs={12} sm={6}>
            <FormLabel label="Action Mode" />
            <SelectInput
              value={form.action || form.action_mode || "all"}
              onChange={e => setField("action", e?.target?.value || e)}
              options={ACTION_MODES}
              optionKey="value"
              optionLabel="label"
              width="100%"
              disabled={isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormLabel label="Authentication Type" />
            <SelectInput
              value={form.auth_type || "password"}
              onChange={e => setField("auth_type", e?.target?.value || e)}
              options={AUTH_TYPES}
              optionKey="value"
              optionLabel="label"
              width="100%"
              disabled={isLoading}
            />
          </Grid>

          {/* Row 3: Destination Host (Target) & Port */}
          <Grid item xs={12} sm={6}>
            <FormLabel label="Destination Host (Target)" />
            <TextInput
              value={form.host}
              onChange={e => setField("host", e.target.value)}
              placeholder="sftp.spotify.com"
              width="100%"
              disabled={isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormLabel label="Port" />
            <TextInput
              value={form.port}
              onChange={e => setField("port", e.target.value)}
              placeholder="22"
              width="100%"
              disabled={isLoading}
            />
          </Grid>

          {/* Row 4: Destination Username & Destination Password */}
          <Grid item xs={12} sm={6}>
            <FormLabel label="Destination Username" />
            <TextInput
              value={form.username}
              onChange={e => setField("username", e.target.value)}
              placeholder="spotify_user"
              width="100%"
              disabled={isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormLabel label="Destination Password" />
            <TextInput
              type="password"
              value={form.password}
              onChange={e => setField("password", e.target.value)}
              placeholder="••••••••••••••••••••"
              width="100%"
              disabled={isLoading}
            />
          </Grid>

          {/* Bastion Host specific fields */}
          {isBastion && (
            <>
              {/* Row 5: Private Key */}
              <Grid item xs={12}>
                <FormLabel label="Private Key" />
                <TextInput
                  value={form.private_key}
                  onChange={e => setField("private_key", e.target.value)}
                  placeholder="-----BEGIN OPENSSH PRIVATE KEY-----&#10;b3BlbnNzaC1rZXktdjEAAAAABG5vbmUAAAAEbm9uZQAAAAAAAAABAAAMwAAAAtz&#10;c2gtZWQyNTUxOQAAAEC4QW3iP2tS9o1v6...&#10;-----END OPENSSH PRIVATE KEY-----"
                  multiline
                  rows={4}
                  width="100%"
                  className={classes.privateKeyInput}
                  disabled={isLoading}
                />
              </Grid>

              {/* Row 6: Bastion SSH Host & Bastion Port */}
              <Grid item xs={12} sm={6}>
                <FormLabel label="Bastion SSH Host" />
                <TextInput
                  value={form.bastion_host}
                  onChange={e => setField("bastion_host", e.target.value)}
                  placeholder="bastion.us-east.partner.net"
                  width="100%"
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormLabel label="Bastion Port" />
                <TextInput
                  value={form.bastion_port}
                  onChange={e => setField("bastion_port", e.target.value)}
                  placeholder="2222"
                  width="100%"
                  disabled={isLoading}
                />
              </Grid>

              {/* Row 7: Bastion Username & Bastion Password */}
              <Grid item xs={12} sm={6}>
                <FormLabel label="Bastion Username" />
                <TextInput
                  value={form.bastion_username}
                  onChange={e => setField("bastion_username", e.target.value)}
                  placeholder="bastion_partner"
                  width="100%"
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormLabel label="Bastion Password" />
                <TextInput
                  type="password"
                  value={form.bastion_password}
                  onChange={e => setField("bastion_password", e.target.value)}
                  placeholder="••••••••••••••••••••"
                  width="100%"
                  disabled={isLoading}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <SecondaryButton
          label="Cancel"
          onClick={onClose}
          disabled={isLoading}
        />
        <Button
          variant="contained"
          className={classes.testButton}
          onClick={handleTestConnection}
          disabled={isLoading}
        >
          {isTesting ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            "Test Connection"
          )}
        </Button>
        <PrimaryButton
          label="Confirm"
          onClick={handleSave}
          loading={isSaving}
          disabled={isLoading}
        />
      </DialogActions>
    </Dialog>
  );
}

export default CreateOrEditDspSftpModal;
